import { HttpErrorResponse } from "@angular/common/http";

/**
 * Normalizes any error thrown by an HTTP call (or a raw error body) into a
 * single, human-readable message.
 *
 * The backend surfaces errors in a few different shapes:
 *   - a typed `ModelError`     -> { code, message }
 *   - ASP.NET ProblemDetails   -> { title, detail, status, errors: { field: [msgs] } }
 *   - a plain string body
 *   - PascalCase variants (.NET default serialization) -> { Message }, { Error }
 * ...and when the request never reaches the server there is no body at all,
 * only an HTTP status. This helper covers all of those and falls back to a
 * translation key when nothing better is available.
 */
export class ApiErrorHelper {
  /**
   * Resolves an error into something displayable. `isKey` tells the caller
   * whether `text` is a verbatim message (from the server) or a translation
   * key that still needs to run through ngx-translate.
   */
  static resolve(error: unknown): { text: string; isKey: boolean } {
    const detail = ApiErrorHelper.extractDetail(error);

    return detail
      ? { text: detail, isKey: false }
      : { text: ApiErrorHelper.statusKey(error), isKey: true };
  }

  /** Pulls a human-readable message out of a known error body shape, if any. */
  static extractDetail(error: unknown): string | undefined {
    const body = error instanceof HttpErrorResponse ? error.error : error;

    if (body == null) {
      return undefined;
    }

    if (typeof body === "string") {
      // Guard against HTML error pages (proxy/gateway responses) — showing raw
      // markup helps no one, so let the status-code fallback take over.
      const trimmed = body.trim();

      return trimmed && !trimmed.startsWith("<") ? trimmed : undefined;
    }

    // Blob bodies (e.g. a failed file download requested as responseType blob)
    // can only be read asynchronously, so we can't extract from them here.
    if (body instanceof Blob) {
      return undefined;
    }

    if (typeof body === "object") {
      return ApiErrorHelper.fromObject(body as Record<string, unknown>);
    }

    return undefined;
  }

  private static fromObject(body: Record<string, unknown>): string | undefined {
    // ProblemDetails validation errors: { errors: { field: ["msg", ...] } }.
    const validation = ApiErrorHelper.fromValidationErrors(body["errors"]);
    if (validation) {
      return validation;
    }

    // Preferred order of single-message fields, covering camelCase and the
    // PascalCase that .NET serializes by default.
    const candidates = [
      body["message"],
      body["Message"],
      body["detail"],
      body["Detail"],
      body["error"],
      body["Error"],
      body["title"],
      body["Title"],
    ];

    const message = candidates.find(
      (value): value is string =>
        typeof value === "string" && value.trim().length > 0
    );

    return message?.trim();
  }

  private static fromValidationErrors(errors: unknown): string | undefined {
    if (!errors || typeof errors !== "object") {
      return undefined;
    }

    const messages = Object.values(errors as Record<string, unknown>)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0
      );

    return messages.length ? messages.join(" ") : undefined;
  }

  /** Maps the HTTP status to a translation key when no body message exists. */
  private static statusKey(error: unknown): string {
    const status = error instanceof HttpErrorResponse ? error.status : 0;

    switch (status) {
      case 0:
        return "ERROR_NETWORK";
      case 400:
        return "ERROR_BAD_REQUEST";
      case 401:
        return "ERROR_UNAUTHORIZED";
      case 403:
        return "ERROR_FORBIDDEN";
      case 404:
        return "ERROR_NOT_FOUND";
      case 408:
        return "ERROR_TIMEOUT";
      case 409:
        return "ERROR_CONFLICT";
      case 422:
        return "ERROR_VALIDATION";
      case 429:
        return "ERROR_TOO_MANY_REQUESTS";
      default:
        return status >= 500 ? "ERROR_SERVER" : "UNKNOWN_ERROR";
    }
  }
}
